// src/components/SEO.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_METADATA, SEO_KEYWORDS } from '../utils/constants';

const SEO = ({
  // common
  title,
  description,
  keywords = [],
  image,
  path,            // e.g., `/insights/some-slug`
  // article-specific
  type = 'website', // 'website' | 'article'
  publishedTime,
  modifiedTime,
  tags = [],
  // structured data
  jsonLd = [],      // array of JSON-LD objects to emit
}) => {
  const siteTitle = title ? `${title} | ${SITE_METADATA.title}` : SITE_METADATA.title;
  const siteDescription = description || SITE_METADATA.description;
  const siteKeywords = [...(SEO_KEYWORDS || []), ...keywords, ...tags].join(', ');

  const siteUrlRoot = SITE_METADATA.siteUrl?.replace(/\/$/, '') || '';
  const pageUrl = path ? `${siteUrlRoot}${path.startsWith('/') ? path : `/${path}`}` : SITE_METADATA.siteUrl;

  // ✅ missing before
  const siteImage = image || `${siteUrlRoot}/og-image.jpg`;
  const isArticle = (type || '').toLowerCase() === 'article';

  const twitterSite = SITE_METADATA.twitter || undefined;
  const ldBlocks = (Array.isArray(jsonLd) ? jsonLd : []).filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={siteDescription} />
      {siteKeywords && <meta name="keywords" content={siteKeywords} />}
      {SITE_METADATA.author && <meta name="author" content={SITE_METADATA.author} />}

      {/* Canonical */}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={isArticle ? 'article' : 'website'} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:site_name" content={SITE_METADATA.title || 'Website'} />
      <meta property="og:image" content={siteImage} />

      {/* Article-only OG */}
      {isArticle && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {isArticle && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {isArticle && tags && tags.map((t, i) => (
        <meta key={`article:tag:${i}`} property="article:tag" content={t} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}

      {/* Robots etc. */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* JSON-LD blocks (Article, FAQ, etc.) */}
      {ldBlocks.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
